import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Clock, Check, Sparkles, ChevronRight, Plus, MessageCircle, MapPin, ExternalLink, Search, MoreVertical, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import VALAssistant from '../components/VALAssistant';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const Checklist = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [weddingPlan, setWeddingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVAL, setShowVAL] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', phase: '', category: 'general' });
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [tempLocation, setTempLocation] = useState('');
  const [pendingSearch, setPendingSearch] = useState(null);
  const [openTaskMenu, setOpenTaskMenu] = useState(null);

  useEffect(() => {
    loadData();
  }, [user.id]);

  // Close task menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openTaskMenu) {
        setOpenTaskMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openTaskMenu]);

  const loadData = async () => {
    try {
      const [tasksRes, planRes] = await Promise.all([
        axios.get(`${API}/tasks/${user.id}`),
        axios.get(`${API}/wedding-plans/${user.id}`)
      ]);
      setTasks(tasksRes.data);
      setWeddingPlan(planRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete a task
  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setTasks(tasks.filter(t => t.id !== taskId));
      setOpenTaskMenu(null);
      toast.success('Task removed');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Handle task action (internal link, search, or Grace)
  const handleTaskAction = (task) => {
    const actionType = task.action_type;
    const actionLink = task.action_link;
    const actionSearch = task.action_search;

    if (actionType === 'internal' && actionLink) {
      // Navigate to internal app page
      navigate(actionLink);
    } else if (actionType === 'val') {
      // Open Grace assistant
      setShowVAL(true);
    } else if (actionType === 'search' && actionSearch) {
      // Perform Google search with location
      performSearch(actionSearch);
    } else {
      // Default: expand/show task details or open Grace for assistance
      setShowVAL(true);
      toast.info(`Ask Grace about: "${task.title}"`);
    }
  };

  // Perform Google search with location
  const performSearch = (searchQuery) => {
    const location = weddingPlan?.venue_location || weddingPlan?.venue;
    
    if (!location) {
      // No location set - prompt user
      setPendingSearch(searchQuery);
      setShowLocationPrompt(true);
      return;
    }
    
    // Open Google search in new tab
    const fullQuery = `${searchQuery} near ${location}`;
    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(fullQuery)}`;
    
    // Open in a new tab - no popup messages needed
    window.open(googleUrl, '_blank', 'noopener,noreferrer');
  };

  // Save location and perform pending search
  const handleLocationSubmit = async () => {
    if (!tempLocation.trim()) {
      toast.error('Please enter your wedding location');
      return;
    }
    
    try {
      await axios.put(`${API}/wedding-plans/${user.id}`, {
        venue_location: tempLocation.trim()
      });
      
      // Update local state
      setWeddingPlan({ ...weddingPlan, venue_location: tempLocation.trim() });
      
      // Perform the pending search
      if (pendingSearch) {
        const fullQuery = `${pendingSearch} near ${tempLocation.trim()}`;
        const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(fullQuery)}`;
        
        // Open in a new tab - don't show any popup messages
        window.open(googleUrl, '_blank', 'noopener,noreferrer');
      }
      
      setShowLocationPrompt(false);
      setTempLocation('');
      setPendingSearch(null);
      toast.success('Location saved!');
    } catch (error) {
      toast.error('Failed to save location');
    }
  };

  // Get action button label and icon for a task
  const getTaskActionInfo = (task) => {
    switch (task.action_type) {
      case 'internal':
        return { label: 'Go', icon: ChevronRight };
      case 'search':
        return { label: 'Search', icon: Search };
      case 'val':
        return { label: 'Chat', icon: MessageCircle };
      default:
        // All tasks should be clickable - default to "View" action
        return { label: 'View', icon: ChevronRight };
    }
  };

  const calculateMonthsUntil = () => {
    if (!weddingPlan?.wedding_date) return null;
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = weddingPlan.wedding_date.split('-').map(Number);
    const wedding = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const months = Math.ceil((wedding - today) / (1000 * 60 * 60 * 24 * 30));
    return Math.max(0, months);
  };

  const monthsUntilWedding = calculateMonthsUntil();

  // Timeline phases with visual representation
  const timelinePhases = [
    { id: 'getting_started', name: 'Getting Started', months: '12+', icon: '🎯', description: 'Set your foundation' },
    { id: '12_months', name: 'Early Planning (12 Months)', months: '12', icon: '📋', description: 'Book major vendors' },
    { id: '9_months', name: '9 Months Before', months: '9', icon: '💐', description: 'Lock in the details' },
    { id: '6_months', name: '6 Months Before', months: '6', icon: '💌', description: 'Send invitations' },
    { id: '3_months', name: '3 Months Before', months: '3', icon: '👗', description: 'Final fittings' },
    { id: '1_month', name: 'Final Month', months: '1', icon: '✨', description: 'Confirm everything' },
    { id: '2_weeks', name: 'Final Two Weeks', months: '0.5', icon: '💍', description: 'Last preparations' },
    { id: 'wedding_week', name: 'Wedding Week', months: '0', icon: '💒', description: 'The big moment!' },
  ];

  // Map task phases to timeline phases
  const getPhaseId = (phase) => {
    if (!phase) return 'getting_started';
    const p = phase.toLowerCase();
    if (p === 'getting started' || p.includes('getting started')) return 'getting_started';
    if (p.includes('12 months') || p.includes('early planning')) return '12_months';
    if (p.includes('9 months') || p.includes('9-11')) return '9_months';
    if (p.includes('6 months') || p.includes('6-8')) return '6_months';
    if (p.includes('3 months') || p.includes('4-5') || p.includes('2-3')) return '3_months';
    if (p.includes('final month') || p.includes('1 month')) return '1_month';
    if (p.includes('two weeks') || p.includes('final two')) return '2_weeks';
    if (p.includes('wedding week') || p === 'week') return 'wedding_week';
    return 'getting_started';
  };

  // Get current phase based on months until wedding
  const getCurrentPhase = () => {
    if (!monthsUntilWedding) return 'getting_started';
    if (monthsUntilWedding > 12) return 'getting_started';
    if (monthsUntilWedding > 9) return '12_months';
    if (monthsUntilWedding > 6) return '9_months';
    if (monthsUntilWedding > 3) return '6_months';
    if (monthsUntilWedding > 1) return '3_months';
    if (monthsUntilWedding > 0.5) return '1_month';
    if (monthsUntilWedding > 0.25) return '2_weeks';
    return 'wedding_week';
  };

  const currentPhase = getCurrentPhase();

  // Group tasks by phase
  const tasksByPhase = tasks.reduce((acc, task) => {
    const phaseId = getPhaseId(task.phase);
    if (!acc[phaseId]) acc[phaseId] = [];
    acc[phaseId].push(task);
    return acc;
  }, {});

  // Check if phase is unlocked (current or past)
  const isPhaseUnlocked = (phaseMonths) => {
    if (!monthsUntilWedding) return phaseMonths === '12+';
    const months = phaseMonths === '12+' ? 13 : parseFloat(phaseMonths);
    return months >= monthsUntilWedding;
  };

  // Check if phase is completed
  const isPhaseCompleted = (phaseId) => {
    const phaseTasks = tasksByPhase[phaseId] || [];
    if (phaseTasks.length === 0) return false;
    return phaseTasks.every(t => t.completed);
  };

  // Toggle task completion
  const toggleTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`);
      setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t));
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Add new task
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.title) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const response = await axios.post(`${API}/tasks`, {
        user_id: user.id,
        title: newTask.title,
        description: newTask.description,
        phase: newTask.phase || timelinePhases.find(p => p.id === currentPhase)?.name,
        category: newTask.category
      });
      setTasks([...tasks, response.data]);
      setNewTask({ title: '', description: '', phase: '', category: 'general' });
      setShowAddTask(false);
      toast.success('Task added!');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  // Calculate overall progress
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.completed).length;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Custom gentle pulse animation */}
      <style>{`
        @keyframes gentlePulse {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(136, 19, 55, 0.4);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 0 8px rgba(136, 19, 55, 0);
            transform: scale(1.02);
          }
        }
      `}</style>
      
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="mb-12">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="text-[#881337]" size={32} />
                <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917]">
                  Your Wedding Journey
                </h1>
              </div>
              <p className="text-[#57534E] text-lg">
                {user.partner1_name} & {user.partner2_name}'s path to "I do"
              </p>
            </div>
            <Button
              onClick={() => setShowVAL(true)}
              className="rounded-full bg-gradient-to-r from-[#881337] to-[#9F1239] hover:from-[#9F1239] hover:to-[#881337] text-white px-6"
            >
              <Sparkles size={18} className="mr-2" />
              Ask Grace
            </Button>
          </div>

          {/* Progress Overview */}
          <div className="bg-white rounded-3xl p-8 border border-stone-200 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Progress Circle */}
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="80" cy="80" r="70" fill="none" stroke="#F5F5F4" strokeWidth="12" />
                  <circle
                    cx="80" cy="80" r="70" fill="none"
                    stroke="url(#progressGradient)" strokeWidth="12" strokeLinecap="round"
                    strokeDasharray={`${progressPercent * 4.4} 440`}
                  />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#881337" />
                      <stop offset="100%" stopColor="#9F1239" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-serif text-[#881337]">{progressPercent}%</span>
                  <span className="text-xs text-[#57534E]">Complete</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center md:text-left">
                  <p className="text-3xl font-serif text-[#1C1917]">{monthsUntilWedding || '--'}</p>
                  <p className="text-sm text-[#57534E]">Months to Go</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-3xl font-serif text-[#881337]">{completedTasks}</p>
                  <p className="text-sm text-[#57534E]">Tasks Done</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-3xl font-serif text-[#1C1917]">{totalTasks - completedTasks}</p>
                  <p className="text-sm text-[#57534E]">Remaining</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-3xl font-serif text-[#84A98C]">{totalTasks}</p>
                  <p className="text-sm text-[#57534E]">Total Tasks</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Task Button */}
        <div className="flex justify-end mb-6">
          <Button onClick={() => setShowAddTask(true)} variant="outline" className="rounded-full">
            <Plus size={18} className="mr-2" />
            Add Task
          </Button>
        </div>

        {/* Add Task Form */}
        {showAddTask && (
          <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-8">
            <h3 className="text-lg font-serif mb-4">Add Custom Task</h3>
            <form onSubmit={handleAddTask} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Task Title *</label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="e.g., Book florist consultation"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Phase</label>
                  <select
                    value={newTask.phase}
                    onChange={(e) => setNewTask({ ...newTask, phase: e.target.value })}
                    className="w-full h-10 rounded-xl border border-stone-200 px-3"
                  >
                    <option value="">Current Phase</option>
                    {timelinePhases.map(phase => (
                      <option key={phase.id} value={phase.name}>{phase.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Optional details..."
                  className="rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddTask(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                  Add Task
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Visual Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#881337] via-[#9F1239] to-[#F5F5F4]" />

          {/* Timeline Phases */}
          <div className="space-y-8">
            {timelinePhases.map((phase) => {
              const phaseTasks = tasksByPhase[phase.id] || [];
              const isUnlocked = isPhaseUnlocked(phase.months);
              const isCompleted = isPhaseCompleted(phase.id);
              const isCurrent = currentPhase === phase.id;
              const isExpanded = selectedPhase === phase.id;

              return (
                <div key={phase.id} className="relative pl-20">
                  {/* Phase Marker */}
                  <div 
                    className={`absolute left-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl border-4 transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-[#84A98C] border-[#84A98C] text-white'
                        : isCurrent
                          ? 'bg-[#881337] border-[#881337] text-white'
                          : isUnlocked
                            ? 'bg-white border-[#881337] text-[#881337]'
                            : 'bg-stone-100 border-stone-300 text-stone-400'
                    }`}
                    style={isCurrent && !isCompleted ? {
                      animation: 'gentlePulse 4s ease-in-out infinite'
                    } : {}}
                  >
                    {isCompleted ? <Check size={28} /> : phase.icon}
                  </div>

                  {/* Phase Card */}
                  <div 
                    className={`rounded-2xl border transition-all duration-300 ${
                      isUnlocked 
                        ? 'bg-white border-stone-200 hover:shadow-lg cursor-pointer' 
                        : 'bg-stone-50 border-stone-200 opacity-60'
                    } ${isCurrent ? 'ring-2 ring-[#881337] ring-offset-2' : ''}`}
                    onClick={() => isUnlocked && setSelectedPhase(isExpanded ? null : phase.id)}
                  >
                    {/* Phase Header */}
                    <div className="p-6 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-serif text-[#1C1917]">{phase.name}</h3>
                          {isCurrent && (
                            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full">
                              You are here
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-xs bg-[#84A98C] text-white px-2 py-0.5 rounded-full">
                              Completed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#57534E] mt-1">
                          {phase.description} • {phaseTasks.length > 0 
                            ? `${phaseTasks.filter(t => t.completed).length}/${phaseTasks.length} tasks`
                            : 'No tasks yet'
                          }
                        </p>
                      </div>
                      {isUnlocked && phaseTasks.length > 0 && (
                        <ChevronRight 
                          size={24} 
                          className={`text-stone-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                    </div>

                    {/* Expanded Tasks */}
                    {isExpanded && phaseTasks.length > 0 && (
                      <div className="border-t border-stone-100 p-6 space-y-3">
                        {phaseTasks.map(task => (
                          <div 
                            key={task.id}
                            className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${
                              task.completed ? 'bg-[#84A98C]/10' : 'bg-stone-50 hover:bg-stone-100'
                            }`}
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleTask(task.id); }}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                task.completed 
                                  ? 'bg-[#84A98C] border-[#84A98C] text-white' 
                                  : 'border-stone-300 hover:border-[#881337]'
                              }`}
                            >
                              {task.completed && <Check size={14} />}
                            </button>
                            <div className="flex-1">
                              <p className={`font-medium ${task.completed ? 'line-through text-stone-400' : 'text-[#1C1917]'}`}>
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-sm text-[#57534E] mt-1">{task.description}</p>
                              )}
                            </div>
                            
                            {/* Action Button */}
                            {!task.completed && getTaskActionInfo(task) && (
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleTaskAction(task); }}
                                className={`rounded-full text-xs px-3 py-1 h-auto flex items-center gap-1 ${
                                  task.action_type === 'search' 
                                    ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                    : task.action_type === 'val'
                                      ? 'bg-gradient-to-r from-[#881337] to-[#9F1239] hover:from-[#9F1239] hover:to-[#881337] text-white'
                                      : 'bg-[#881337] hover:bg-[#9F1239] text-white'
                                }`}
                              >
                                {React.createElement(getTaskActionInfo(task).icon, { size: 12 })}
                                {getTaskActionInfo(task).label}
                                {task.action_type === 'search' && <ExternalLink size={10} className="ml-1" />}
                              </Button>
                            )}

                            {/* Three-dot Menu */}
                            <div className="relative">
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setOpenTaskMenu(openTaskMenu === task.id ? null : task.id);
                                }}
                                className="p-1 text-stone-400 hover:text-stone-600 transition-colors"
                              >
                                <MoreVertical size={18} />
                              </button>
                              
                              {/* Dropdown Menu */}
                              {openTaskMenu === task.id && (
                                <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-20 min-w-[140px]">
                                  <button
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      handleDeleteTask(task.id);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <Trash2 size={14} />
                                    Remove Task
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Locked State */}
                    {!isUnlocked && (
                      <div className="p-6 pt-0">
                        <p className="text-sm text-stone-400 italic">
                          🔒 Unlocks when you're closer to your wedding date
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grace CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-3xl p-8 text-white text-center">
          <Sparkles className="mx-auto mb-4" size={32} />
          <h3 className="text-2xl font-serif mb-2">Need Help With Your Journey?</h3>
          <p className="text-rose-100 mb-6 max-w-xl mx-auto">
            Grace can help customize your planning tasks based on your wedding style, budget, and priorities.
          </p>
          <Button
            onClick={() => setShowVAL(true)}
            className="bg-white text-[#881337] hover:bg-rose-50 rounded-full px-8"
          >
            <MessageCircle size={18} className="mr-2" />
            Chat with Grace
          </Button>
        </div>
      </div>

      {/* Grace Assistant */}
      {showVAL && (
        <VALAssistant 
          user={user} 
          onClose={() => setShowVAL(false)}
          onMessageSent={loadData}
        />
      )}

      {/* Location Prompt Modal */}
      {showLocationPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-serif font-medium text-white">Where&apos;s Your Wedding?</h3>
                  <p className="text-xs text-blue-100">We&apos;ll search for vendors in your area</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-[#57534E] mb-4">
                Enter your wedding city or area to find local vendors.
              </p>
              <Input
                type="text"
                value={tempLocation}
                onChange={(e) => setTempLocation(e.target.value)}
                placeholder="e.g., Austin, TX or Los Angeles, CA"
                className="rounded-xl"
                autoFocus
              />
            </div>
            
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowLocationPrompt(false); setPendingSearch(null); setTempLocation(''); }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLocationSubmit}
                className="rounded-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Search size={16} className="mr-2" />
                Search
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checklist;
